import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  List,
  Button,
  Divider,
  ListItem,
  Typography,
  ListItemText,
  ListItemIcon,
} from '@material-ui/core';
import {
  PaymentInfo,
  PaymentIcon,
  PaymentNumber,
} from 'components/PaymentMethods/PaymentComponents';

import i18n from 'i18n-js';
import { buildUrl } from 'utils';
import { useQuery } from 'hooks';
import { Card } from 'components/UI';
import Loading from 'components/Loading';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { OrganizationsActions } from 'state/modules/organizations';

const listTextStyles = makeStyles((theme) => {
  return {
    primary: {
      fontSize: '13px',
      fontWeight: '600',
      color: '#4D4F5C',
      wordBreak: 'break-word',
    },

    secondary: {
      color: theme.palette.common.lightNavy,
      fontSize: '13px',
      wordBreak: 'break-word',
    },
  };
});

export const PaymentBrokersWidget = () => {
  const urlParams = useQuery();
  const history = useHistory();
  const { code } = urlParams;
  const dispatch = useDispatch();
  const listClasses = listTextStyles();

  const { organization, account, accountDetails, loading, fetchingAccount } = useSelector(
    (state) => state.organizations
  );

  const handleOnboard = () => {
    dispatch(OrganizationsActions.fetchOnboard(organization?.id));
  };

  const handleLinkAccountSuccess = useCallback(() => {
    dispatch(OrganizationsActions.fetchOrganizationById(organization?.id));
    history.replace(buildUrl(`/general-information`));
  }, [dispatch, organization?.id]);

  const handleLinkAccount = () => {
    dispatch(OrganizationsActions.fetchLinkAccount(organization?.id));
  };

  const handleCreateAccount = useCallback(
    (payload = {}, onSuccess) => {
      if (organization?.id) {
        dispatch(OrganizationsActions.fetchCreateAccount(organization?.id, payload, onSuccess));
      }
    },
    [dispatch, organization?.id]
  );

  useEffect(() => {
    if (code) {
      handleCreateAccount({ code }, handleLinkAccountSuccess);
    }
  }, [code, handleCreateAccount, handleLinkAccountSuccess]);

  useEffect(() => {
    if (account?.url && account?.redirect) {
      window.open(account?.url, '_self');
    }
  }, [account?.url]);

  useEffect(() => {
    if (organization?.has_payment_account_created) {
      dispatch(OrganizationsActions.fetchAccountDetails(organization?.id));
    }
  }, [organization?.has_payment_account_created, dispatch]);

  return (
    <Card title={i18n.t('organization.payment_brokers_title')} headerActionType="vertical">
      {loading || fetchingAccount ? (
        <Box
          style={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            minHeight: 100,
          }}
        >
          <Loading />
        </Box>
      ) : (
        <Box>
          <Typography variant="h3">
            <Box fontSize={13} mb={3}>
              {i18n.t(
                `organization.${
                  !organization?.has_payment_account_created
                    ? 'payment_brokers_description_first_state'
                    : accountDetails?.status === 'ONBOARDING'
                      ? 'payment_brokers_description_second_state'
                      : accountDetails?.status === 'ONBOARDINGCOMPLETED'
                        ? 'payment_brokers_description_third_state'
                        : 'payment_brokers_description_fourth_state'
                }`
              )}
            </Box>
          </Typography>
          {accountDetails?.payment_account?.last_four && (
            <>
              <Divider />
              <List>
                <ListItem disableGutters>
                  <ListItemIcon>
                    <PaymentIcon type="ach" />
                  </ListItemIcon>
                  <ListItemText
                    classes={listClasses}
                    primary={<PaymentNumber data={accountDetails?.payment_account} />}
                    secondary={<PaymentInfo data={accountDetails?.payment_account} />}
                  />
                </ListItem>
              </List>
            </>
          )}
          {!organization?.has_payment_account_created && !accountDetails?.status && (
            <>
              <Button
                color="primary"
                variant="outlined"
                disabled={fetchingAccount}
                data-cy="create-payment-account-btn"
                onClick={() => handleCreateAccount()}
                style={{ textTransform: 'capitalize', width: '100%' }}
              >
                {i18n.t('organization.create_account')}
              </Button>
              <Box mt={2}>
                <Button
                  color="primary"
                  variant="outlined"
                  disabled={fetchingAccount}
                  onClick={handleLinkAccount}
                  data-cy="link-payment-account-btn"
                  style={{ textTransform: 'capitalize', width: '100%' }}
                >
                  {i18n.t('organization.link_account')}
                </Button>
              </Box>
            </>
          )}
          {organization?.has_payment_account_created &&
            ['ONBOARDING'].includes(accountDetails?.status) && (
              <Button
                color="primary"
                variant="outlined"
                data-cy="onboard-btn"
                onClick={handleOnboard}
                disabled={fetchingAccount}
                style={{ textTransform: 'capitalize', width: '100%' }}
              >
                {i18n.t('organization.onboard')}
              </Button>
            )}
        </Box>
      )}
    </Card>
  );
};
